'use client'

import { createContext, ReactNode, useContext, useEffect, useState, useMemo } from 'react'

interface IGlobalContextType {
  isSidebarOpen: boolean
  userInteracted: boolean
  isAuthEnable: boolean
  toggleSidebar: (state: boolean) => void
  isLoggedIn: boolean
  setIsLoggedIn: (state: boolean) => void
  host: string
}

const GlobalContext = createContext<IGlobalContextType>({
  isSidebarOpen: false,
  userInteracted: false,
  isAuthEnable: false,
  toggleSidebar: async () => {},
  isLoggedIn: false,
  setIsLoggedIn: async () => {},
  host: '',
})

interface GlobalProviderProps {
  children: ReactNode
  isAuthEnable: boolean
  host: string
}

export const GlobalProvider = ({ children, isAuthEnable, host }: GlobalProviderProps) => {
  const [isSidebarOpen, toggleSidebar] = useState(false)
  const [userInteracted, setUserInteracted] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const triggerUserInteracted = () => {
    setUserInteracted(true)
  }

  useEffect(() => {
    ;['click', 'mousemove', 'mouseover', 'mousemove', 'touchmove', 'focus', 'wheel'].forEach(eventName => {
      window.addEventListener(eventName, triggerUserInteracted)
      setTimeout(() => {
        setUserInteracted(true)
      }, 10000)
      if (userInteracted) {
        window.removeEventListener(eventName, triggerUserInteracted)
      }
      return () => {
        window.removeEventListener(eventName, triggerUserInteracted)
      }
    })
  }, [userInteracted])

  const contextValue = useMemo(
    () => ({
      isSidebarOpen,
      userInteracted,
      toggleSidebar,
      isAuthEnable,
      isLoggedIn,
      setIsLoggedIn,
      host,
    }),
    [isSidebarOpen, userInteracted, isAuthEnable, isLoggedIn, host],
  )

  return <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>
}

export const useGlobal = (): IGlobalContextType => {
  const context = useContext(GlobalContext)
  if (!context) {
    throw new Error('useGlobal must be used within an GlobalProvider')
  }
  return context
}
